import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch } from '../../store/hooks';
import { fetchWhatsAppTemplates } from './whatsAppMessageThunks';
import type {
  WhatsAppTemplate,
  WhatsAppWindowConflict,
} from './whatsAppMessageTypes';

/**
 * WHATSAPP TEMPLATE PICKER — compare quando il backend rifiuta un invio perché la
 * sessione è scaduta (409). Fuori dalle 24 ore Meta accetta solo modelli approvati:
 * non è una nostra scelta e non è aggirabile.
 *
 * Ha due facce, secondo cosa dice il conflitto:
 *
 * - il conflitto porta un **template di ricontatto** (configurato e approvato) → si
 *   chiede solo **conferma**, mostrando il testo che partirà davvero. Il server
 *   potrebbe inviarlo da sé, e all'inizio lo faceva: ma il contatto riceve il testo
 *   *dentro* un modello approvato, non il testo scritto — e un modello è a pagamento.
 *   Sono due cose che l'utente deve sapere **prima** di premere invio, non dopo.
 * - il conflitto **non** porta un template → si fa **scegliere** fra quelli approvati.
 *
 * In entrambi i casi l'anteprima è il testo **reso**, coi segnaposto già sostituiti:
 * mostrare il modello grezzo con `{{1}}` dentro sarebbe mostrare una cosa che non
 * esiste.
 *
 * Inviare un modello **non riapre la sessione scaduta**: serve solo a far uscire il
 * messaggio e a far nascere un nuovo raggruppamento. La sessione la riapre soltanto
 * una risposta del contatto.
 */
interface WhatsAppTemplatePickerProps {
  conflict: WhatsAppWindowConflict;
  pendingBody: string;
  submitting: boolean;
  onClose: () => void;
  onSend: (template: WhatsAppTemplate, parameters: string[]) => void;
}

const FORM_ID = 'whatsapp-template-form';

export function WhatsAppTemplatePicker({
  conflict,
  pendingBody,
  submitting,
  onClose,
  onSend,
}: WhatsAppTemplatePickerProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Template imposto dalla configurazione: niente scelta, solo conferma.
  const preset = conflict.template ?? null;

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>(
    preset ? [preset] : [],
  );
  const [loading, setLoading] = useState(!preset);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<WhatsAppTemplate | null>(preset);
  const [parameters, setParameters] = useState<string[]>(
    initialParameters(preset, pendingBody),
  );

  useEffect(() => {
    if (preset) return;

    let cancelled = false;

    dispatch(fetchWhatsAppTemplates())
      .unwrap()
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
        const first = list[0] ?? null;
        setSelected(first);
        setParameters(initialParameters(first, pendingBody));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, pendingBody, preset]);

  function pick(name: string) {
    const template = templates.find((tpl) => tpl.name === name) ?? null;
    setSelected(template);
    setParameters(initialParameters(template, pendingBody));
  }

  function setParameter(index: number, value: string) {
    setParameters((prev) => prev.map((p, i) => (i === index ? value : p)));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (selected) onSend(selected, parameters);
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">
          {preset
            ? t('whatsapp.template.confirmTitle')
            : t('whatsapp.template.title')}
        </span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <Alert severity="info" className="wa-template__notice">
          {t('whatsapp.template.notice')}
        </Alert>

        {loading && (
          <p className="wa-template__hint">{t('whatsapp.template.loading')}</p>
        )}

        {!loading && error && (
          <Alert severity="error" className="wa-template__notice">
            {t('whatsapp.template.loadError')}
          </Alert>
        )}

        {!loading && !error && templates.length === 0 && (
          <Alert severity="warning" className="wa-template__notice">
            {t('whatsapp.template.none')}
          </Alert>
        )}

        {selected && (
          <form id={FORM_ID} onSubmit={handleSubmit} className="whatsapp-form">
            {!preset && (
              <TextField
                select
                label={t('whatsapp.template.label')}
                value={selected.name}
                onChange={(e) => pick(e.target.value)}
                fullWidth
                className="form-field"
              >
                {templates.map((tpl) => (
                  <MenuItem key={`${tpl.name}:${tpl.language}`} value={tpl.name}>
                    {tpl.name} ({tpl.language})
                  </MenuItem>
                ))}
              </TextField>
            )}

            {parameters.map((value, index) => (
              <TextField
                key={index}
                // Il primo segnaposto è il messaggio dell'utente; gli altri, se il
                // modello ne ha, sono campi generici.
                label={
                  index === 0
                    ? t('whatsapp.template.parameter')
                    : t('whatsapp.template.parameterN', { index: index + 1 })
                }
                value={value}
                onChange={(e) => setParameter(index, e.target.value)}
                required
                fullWidth
                multiline
                minRows={2}
                className="form-field"
              />
            ))}

            <p className="wa-template__preview-label">
              {t('whatsapp.template.preview')}
            </p>
            <p className="wa-template__preview">
              {render(selected, parameters)}
            </p>
          </form>
        )}
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          disabled={submitting || !selected}
        >
          {t('whatsapp.template.send')}
        </Button>
        <Button type="button" onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Il primo segnaposto parte col testo che l'utente aveva scritto: è quasi sempre il
 * messaggio da recapitare. Gli altri, se ci sono, restano vuoti.
 */
function initialParameters(
  template: WhatsAppTemplate | null,
  pendingBody: string,
): string[] {
  if (!template) return [];

  return Array.from({ length: template.parameterCount }, (_, i) =>
    i === 0 ? pendingBody : '',
  );
}

/**
 * Testo che il contatto riceverà davvero.
 *
 * `replaceAll`, non `replace`: il server (`WhatsAppTemplate.render`) usa `String.replace` di Java,
 * che sostituisce TUTTE le occorrenze, mentre in JavaScript `replace` con una stringa ne sostituisce
 * solo la PRIMA. Con `replace`, un template che ripete `{{1}}` mostrava un'anteprima diversa dal
 * messaggio che partiva — cioè falliva l'unica cosa per cui questa funzione esiste.
 */
function render(template: WhatsAppTemplate, parameters: string[]): string {
  return parameters.reduce(
    (text, value, i) => text.replaceAll(`{{${i + 1}}}`, value),
    template.body,
  );
}

export default WhatsAppTemplatePicker;
